import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { PanelMenu } from "primereact/panelmenu"
import "primereact/resources/themes/saga-blue/theme.css"
import "primereact/resources/primereact.min.css"
import "primeicons/primeicons.css"
import "../../styles/primesidebar.css"
import packageInfo from "../../../package.json"
import PPSelectCustomers from "../admin/PPSelectCustomers"
import PPUtils from "../../utils/PPUtils"
import piranhaIcon from "../../img/piranha.svg"
import { isDematchingPath, isMatchingPath, isMatchingSectionPath, isPriceManagementPath } from "./ppNavPaths"

function routeClass(active, extra = "") {
    return [active ? "pp-nav-active" : "", extra].filter(Boolean).join(" ")
}

function isExactPath(pathname, path) {
    return pathname === path || pathname === `${path}/`
}

function isPrefixPath(pathname, path) {
    return pathname === path || pathname.startsWith(`${path}/`)
}

export default function PPPrimeSidebar({
    lightMode,
    setPresetId,
    auth,
    authName,
    authAvatarUrl,
    productCount,
    hasPriceManagementAccess
}) {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const pathname = location.pathname
    const hasProviderSummaryAccess = auth.hasProviderSummaryAccess()
    const hasPpAiAccess = auth.hasPpAiAccess()
    const isAdmin = auth.isAdmin()

    const [expandedKeys, setExpandedKeys] = useState(() => {
        const keys = {}
        if (isMatchingSectionPath(window.location.pathname)) {
            keys.matching = true
        }
        if (isPriceManagementPath(window.location.pathname) && hasPriceManagementAccess) {
            keys.priceman = true
        }
        return keys
    })

    useEffect(() => {
        setExpandedKeys((keys) => {
            const next = { ...keys }
            if (isMatchingSectionPath(pathname)) {
                next.matching = true
            }
            if (isPriceManagementPath(pathname) && hasPriceManagementAccess) {
                next.priceman = true
            }
            return next
        })
    }, [pathname, hasPriceManagementAccess])

    const model = useMemo(() => {
        const go = (path) => () => navigate(path)
        const items = [
            {
                key: "dashboard",
                label: t("app.menu.dashboard"),
                icon: "fas fa-th",
                className: routeClass(isExactPath(pathname, "/")),
                command: go("/")
            },
            {
                key: "pricetable",
                label: productCount
                    ? `${t("app.menu.pricetable")} (${productCount})`
                    : t("app.menu.pricetable"),
                icon: "fas fa-edit",
                className: routeClass(isPrefixPath(pathname, "/pricetable")),
                command: go("/pricetable")
            },
            {
                key: "providerSummary",
                label: t("app.menu.provider_summary"),
                icon: "fas fa-table",
                className: routeClass(
                    isPrefixPath(pathname, "/providerSummary"),
                    hasProviderSummaryAccess ? "" : "disabled-nav"
                ),
                command: go("/providerSummary")
            },
            {
                key: "ppai",
                label: t("app.menu.ppai"),
                icon: <img src={piranhaIcon} className="pp-ai-nav-icon" alt="" />,
                className: routeClass(isPrefixPath(pathname, "/ppai"), hasPpAiAccess ? "" : "disabled-nav"),
                command: go("/ppai")
            },
            {
                key: "matching",
                label: t("app.menu.matching"),
                icon: "fas fa-check",
                className: routeClass(isMatchingSectionPath(pathname)),
                items: [
                    {
                        key: "matching_assign",
                        label: t("app.menu.matching_assign"),
                        icon: "fas fa-check",
                        className: routeClass(isMatchingPath(pathname)),
                        command: go("/matching")
                    },
                    {
                        key: "matching_links",
                        label: t("app.menu.matching_links"),
                        icon: "fas fa-link",
                        className: routeClass(isDematchingPath(pathname)),
                        command: go("/dematching")
                    }
                ]
            },
            {
                key: "statistics",
                label: t("app.menu.statistics"),
                icon: "fas fa-chart-bar",
                className: routeClass(isPrefixPath(pathname, "/statistics")),
                command: go("/statistics")
            },
            {
                key: "reports",
                label: t("app.menu.reports"),
                icon: "fas fa-chart-pie",
                className: routeClass(isPrefixPath(pathname, "/reports")),
                command: go("/reports")
            },
            {
                key: "priceman",
                label: t("app.menu.pricemanagementsuite"),
                icon: "fas fa-tasks",
                disabled: !hasPriceManagementAccess,
                className: routeClass(
                    isPriceManagementPath(pathname) && hasPriceManagementAccess,
                    hasPriceManagementAccess ? "" : "disabled-nav"
                ),
                items: hasPriceManagementAccess
                    ? [
                          {
                              key: "priceman_rules",
                              label: t("pricemanagement_page.taps.rule_tap_title"),
                              icon: "fas fa-gavel",
                              className: routeClass(isPrefixPath(pathname, "/pricemanagement/rules")),
                              command: go("/pricemanagement/rules")
                          },
                          {
                              key: "priceman_editor",
                              label: t("pricemanagement_page.taps.editor_tap_title"),
                              icon: "fas fa-edit",
                              className: routeClass(isPrefixPath(pathname, "/pricemanagement/editor")),
                              command: go("/pricemanagement/editor")
                          },
                          {
                              key: "priceman_checklist",
                              label: t("pricemanagement_page.taps.checklist_tap_title"),
                              icon: "fas fa-search",
                              className: routeClass(isPrefixPath(pathname, "/pricemanagement/checklist")),
                              command: go("/pricemanagement/checklist")
                          },
                          {
                              key: "priceman_history",
                              label: t("pricemanagement_page.taps.history_tap_title"),
                              icon: "fas fa-history",
                              className: routeClass(isPrefixPath(pathname, "/pricemanagement/history")),
                              command: go("/pricemanagement/history")
                          },
                          {
                              key: "priceman_excluded",
                              label: t("pricemanagement_page.taps.excluded_articles_tap_title"),
                              icon: "fas fa-eye-slash",
                              className: routeClass(isPrefixPath(pathname, "/pricemanagement/excludedArticles")),
                              command: go("/pricemanagement/excludedArticles")
                          },
                          {
                              key: "priceman_global",
                              label: t("pricemanagement_page.taps.global_rules_tap_title"),
                              icon: "fas fa-cog",
                              className: routeClass(isPrefixPath(pathname, "/pricemanagement/globalRules")),
                              command: go("/pricemanagement/globalRules")
                          }
                      ]
                    : undefined
            }
        ]

        items.push(
            {
                key: "pricealerts",
                label: t("app.menu.pricealerts"),
                icon: "fas fa-bell",
                className: routeClass(isPrefixPath(pathname, "/pricealerts")),
                command: go("/pricealerts")
            },
            {
                key: "settings",
                label: t("app.menu.settings"),
                icon: "fas fa-cogs",
                className: routeClass(isPrefixPath(pathname, "/settings")),
                command: go("/settings")
            },
            {
                key: "admin",
                label: isAdmin ? "Admin" : t("single.shops"),
                icon: "fas fa-tools",
                className: routeClass(isPrefixPath(pathname, "/admin")),
                command: go("/admin")
            }
        )

        if (isAdmin) {
            items.push({
                key: "crawler",
                label: t("app.menu.crawler"),
                icon: "fas fa-spider",
                className: routeClass(isPrefixPath(pathname, "/crawler")),
                command: go("/crawler")
            })
        }

        items.push({
            key: "logout",
            label: t("app.menu.logout"),
            icon: "fas fa-sign-out-alt",
            command: () => {
                auth.signout()
                navigate("/loginpage")
            }
        })

        return items
    }, [
        t,
        navigate,
        pathname,
        productCount,
        hasProviderSummaryAccess,
        hasPpAiAccess,
        hasPriceManagementAccess,
        isAdmin,
        auth
    ])

    return (
        <aside
            className={`main-sidebar elevation-3 pp-prime-sidebar ${
                lightMode == true ? "sidebar-light-primary" : "sidebar-dark-primary"
            }`}>
            <div className="sidebar">
                <div id="sidebar-logo"></div>
                <PPSelectCustomers changePresetId={setPresetId} />
                <div className="user-panel pt-3 pb-3 mb-3 d-flex align-items-center">
                    <div className="image">
                        {authAvatarUrl ? (
                            <img src={authAvatarUrl} className="img-circle elevation-2" alt="" />
                        ) : (
                            PPUtils.getDummyAvatarString(authName, 33, "img-circle elevation-2")
                        )}
                    </div>
                    <div className="info">
                        <span className="d-block">{authName}</span>
                    </div>
                </div>
                <nav className="mt-2 pp-prime-sidebar-nav">
                    <PanelMenu
                        className="pp-prime-nav"
                        model={model}
                        multiple
                        expandedKeys={expandedKeys}
                        onExpandedKeysChange={setExpandedKeys}
                        expandIcon="pi pi-chevron-down"
                        collapseIcon="pi pi-chevron-up"
                        transitionOptions={{ timeout: 200 }}
                    />
                    <a
                        href="https://preis-beobachtung.com/blog/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pp-prime-blog"
                        style={{ color: "#b21a17", fontWeight: "bold" }}>
                        <i className="fas fa-rss"></i>
                        <p>Blog</p>
                    </a>
                </nav>
            </div>
            <div className="sidebarFooter">
                <div className="normal_box">
                    <strong>
                        {t("pp.title")} {packageInfo.version} - {t("pp.copyright")} &copy;{" "}
                        <span style={{ whiteSpace: "nowrap" }}>2014-{new Date().getFullYear()}</span>{" "}
                        <a href="https://www.sylphen.com">Sylphen</a>.
                    </strong>
                    &nbsp;{t("pp.all_rights_reserved")}.
                </div>
                <div className="small_box">
                    <strong>PP {packageInfo.version}</strong>
                </div>
            </div>
        </aside>
    )
}
